import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { passById, type PassId } from '../data/passes'
import { costPerDay } from './roi'
import { loadState, saveState, uid } from './storage'
import type {
  AppState,
  Profile,
  RsvpStatus,
  SkiDay,
  Stamp,
  StampStatus,
  Trip,
  TripRsvp,
} from './types'

interface Store {
  state: AppState
  setProfile: (p: Partial<Profile>) => void
  setStamp: (resortId: string, status: StampStatus | null, date?: string) => void
  addDay: (resortId: string, date: string) => void
  removeDay: (id: string) => void
  createTrip: (input: {
    resortId: string
    startDate: string
    endDate: string
    hostName: string
    note?: string
    hostAvailability?: string[]
  }) => Trip
  updateTrip: (tripId: string, patch: Partial<Pick<Trip, 'hostAvailability' | 'note'>>) => void
  updateTripRsvps: (tripId: string, rsvps: TripRsvp[]) => void
  deleteTrip: (tripId: string) => void
  metrics: {
    visited: number
    want: number
    days: number
    cpd: number | null
    passPrice: number
  }
}

const Ctx = createContext<Store | null>(null)

export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(() => loadState())

  useEffect(() => {
    saveState(state)
  }, [state])

  const setProfile = useCallback((p: Partial<Profile>) => {
    setState((s) => ({ ...s, profile: { ...s.profile, ...p } }))
  }, [])

  const setStamp = useCallback((resortId: string, status: StampStatus | null, date?: string) => {
    setState((s) => {
      const rest = s.stamps.filter((t) => t.resortId !== resortId)
      if (!status) return { ...s, stamps: rest }
      const next: Stamp = { resortId, status, ...(date ? { date } : {}) }
      return { ...s, stamps: [...rest, next] }
    })
  }, [])

  const addDay = useCallback((resortId: string, date: string) => {
    const day: SkiDay = { id: uid('day'), resortId, date }
    setState((s) => {
      const stamps = s.stamps.some((t) => t.resortId === resortId && t.status === 'visited')
        ? s.stamps
        : [
            ...s.stamps.filter((t) => t.resortId !== resortId),
            { resortId, status: 'visited' as const, date },
          ]
      return { ...s, days: [...s.days, day], stamps }
    })
  }, [])

  const removeDay = useCallback((id: string) => {
    setState((s) => ({ ...s, days: s.days.filter((d) => d.id !== id) }))
  }, [])

  const createTrip = useCallback(
    (input: {
      resortId: string
      startDate: string
      endDate: string
      hostName: string
      note?: string
      hostAvailability?: string[]
    }) => {
      const trip: Trip = {
        id: uid('trip'),
        resortId: input.resortId,
        startDate: input.startDate,
        endDate: input.endDate,
        hostName: input.hostName,
        note: input.note,
        hostAvailability: input.hostAvailability ?? [],
        createdAt: new Date().toISOString(),
        rsvps: [],
      }
      setState((s) => ({ ...s, trips: [trip, ...s.trips] }))
      return trip
    },
    [],
  )

  const updateTrip = useCallback(
    (tripId: string, patch: Partial<Pick<Trip, 'hostAvailability' | 'note'>>) => {
      setState((s) => ({
        ...s,
        trips: s.trips.map((t) => (t.id === tripId ? { ...t, ...patch } : t)),
      }))
    },
    [],
  )

  const updateTripRsvps = useCallback((tripId: string, rsvps: TripRsvp[]) => {
    setState((s) => ({
      ...s,
      trips: s.trips.map((t) => (t.id === tripId ? { ...t, rsvps } : t)),
    }))
  }, [])

  const deleteTrip = useCallback((tripId: string) => {
    setState((s) => ({ ...s, trips: s.trips.filter((t) => t.id !== tripId) }))
  }, [])

  const metrics = useMemo(() => {
    const visited = state.stamps.filter((t) => t.status === 'visited').length
    const want = state.stamps.filter((t) => t.status === 'want').length
    const days = state.days.length
    const passPrice = passById(state.profile.passId as PassId).priceUsd
    return {
      visited,
      want,
      days,
      cpd: costPerDay(state.profile.passId, days),
      passPrice,
    }
  }, [state])

  const value: Store = {
    state,
    setProfile,
    setStamp,
    addDay,
    removeDay,
    createTrip,
    updateTrip,
    updateTripRsvps,
    deleteTrip,
    metrics,
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useStore() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useStore outside provider')
  return ctx
}

export type { RsvpStatus }
